import { useEffect } from "react";
import {
  Button,
  PocketRow,
  PocketSection,
  ToggleSwitch,
} from "@/components/ui";
import { useT } from "@/i18n";
import { useScreenshotSettings } from "@/desktop/screenshot-settings";
import { settingsPreferencesMessages } from "@/i18n/messages/settings-preferences";
import { openExternalUrl } from "@/lib/open-external-url";
import {
  retryPrivacySync,
  setPrivacyConsent,
  usePrivacyConsent,
} from "@/lib/privacy-consent";
import { useUsageDataStore } from "@/stores/usage-data-store";

export function PrivacyPage() {
  const t = useT(settingsPreferencesMessages);
  const consent = usePrivacyConsent();
  const usageData = useUsageDataStore((state) => state.enabled);
  const usageDataFailed = useUsageDataStore((state) => state.failed);
  const loadUsageData = useUsageDataStore((state) => state.load);
  const setUsageData = useUsageDataStore((state) => state.setEnabled);
  const syncScreenshots = useScreenshotSettings(
    (state) => state.syncScreenshots,
  );
  const setSyncScreenshots = useScreenshotSettings(
    (state) => state.setSyncScreenshots,
  );
  useEffect(() => {
    void loadUsageData();
  }, [loadUsageData]);
  return (
    <PocketSection testId="settings-page-preferences-privacy">
      <p className="px-2 pb-3 text-appkit-small text-muted">
        {t("privacyIntro")}
      </p>
      <PocketRow
        testId="settings-row-privacy-usage-data"
        title={t("usageData")}
        secondLine={
          <span className="block whitespace-normal">
            {t("usageDataDetail")}
          </span>
        }
        trailing={
          <ToggleSwitch
            checked={usageData ?? false}
            disabled={usageData === null}
            onCheckedChange={(enabled) => void setUsageData(enabled)}
            aria-label={t("usageData")}
            data-testid="privacy-usage-data-toggle"
          />
        }
      />
      {usageDataFailed ? (
        <p
          role="alert"
          className="px-2 pb-3 text-appkit-small text-muted"
          data-testid="privacy-usage-data-error"
        >
          {t("usageDataSyncFailed")}
          <Button
            size="sm"
            variant="ghost"
            data-testid="privacy-usage-data-retry"
            onClick={() => void loadUsageData()}
          >
            {t("retrySync")}
          </Button>
        </p>
      ) : null}
      <PocketRow
        testId="settings-row-privacy-screenshot-sync"
        title={t("syncScreenshots")}
        secondLine={
          <span className="block whitespace-normal">
            {t("syncScreenshotsDetail")}
          </span>
        }
        trailing={
          <ToggleSwitch
            checked={syncScreenshots}
            onCheckedChange={setSyncScreenshots}
            aria-label={t("syncScreenshots")}
            data-testid="privacy-screenshot-sync-toggle"
          />
        }
      />
      <PocketRow
        testId="settings-row-privacy-analytics"
        title={t("productAnalytics")}
        secondLine={
          <span className="block whitespace-normal">
            {t("productAnalyticsDetail")}
          </span>
        }
        trailing={
          <ToggleSwitch
            checked={consent.analyticsSetting}
            onCheckedChange={(analytics) => setPrivacyConsent({ analytics })}
            aria-label={t("productAnalytics")}
            data-testid="privacy-analytics-toggle"
          />
        }
      />
      <PocketRow
        testId="settings-row-privacy-diagnostics"
        title={t("errorReports")}
        secondLine={
          <span className="block whitespace-normal">
            {t("errorReportsDetail")}
          </span>
        }
        trailing={
          <ToggleSwitch
            checked={consent.diagnostics}
            onCheckedChange={(diagnostics) =>
              setPrivacyConsent({ diagnostics })
            }
            aria-label={t("errorReports")}
            data-testid="privacy-diagnostics-toggle"
          />
        }
      />
      <Button
        size="sm"
        variant="ghost"
        data-testid="privacy-policy-link"
        onClick={() => void openExternalUrl("https://www.denker.ai/privacy")}
      >
        {t("privacyPolicy")}
      </Button>
      {consent.error ? (
        <p
          role="alert"
          className="px-2 pt-3 text-appkit-small text-muted"
          data-testid="privacy-sync-error"
        >
          {consent.error}
          <Button
            size="sm"
            variant="ghost"
            data-testid="privacy-sync-retry"
            onClick={retryPrivacySync}
          >
            {t("retrySync")}
          </Button>
        </p>
      ) : null}
    </PocketSection>
  );
}
